import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Box,
} from '@mui/material';
import MuiPhoneNumber from 'material-ui-phone-number';
import useAPI from '../../hooks/useAPI';
import OtpInput from '../../components/otp-input';

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: (response?: any) => void;
  isSwitching?: boolean;
  isAlreadySetUp?: boolean;
  existingPhoneNumber?: string;
  enableCompanyMfaAfterSetup?: boolean;
  email?: string; // Optional email for login-time setup (no auth required)
}

const SmsSetupDialog: React.FC<Props> = ({ open, onClose, onSuccess, isSwitching = false, isAlreadySetUp = false, existingPhoneNumber, enableCompanyMfaAfterSetup = false, email }) => {
  const { t } = useTranslation();
  const API = useAPI();
  const [phoneNumber, setPhoneNumber] = useState(existingPhoneNumber || '');
  const [codeSent, setCodeSent] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [sendingCode, setSendingCode] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [verificationAttempted, setVerificationAttempted] = useState(false);

  React.useEffect(() => {
    if (open && isSwitching && isAlreadySetUp && existingPhoneNumber && !codeSent) {
      // When switching and already set up, automatically send code to existing phone
      const autoSendCode = async () => {
        try {
          setSendingCode(true);
          setError(null);
          await API.setupSms(existingPhoneNumber);
          setCodeSent(true);
        } catch (err: any) {
          console.error('[SMS Setup] Error:', err);
          setError(err?.response?.data?.message || t('sentences:pages.mfa_setup.sms_setup_error') || 'Failed to send verification code');
        } finally {
          setSendingCode(false);
        }
      };
      autoSendCode();
    }
  }, [open, isSwitching, isAlreadySetUp, existingPhoneNumber, codeSent]);

  const handleSendCode = async () => {
    if (!phoneNumber || phoneNumber.trim().length === 0) {
      setError(t('sentences:pages.mfa_setup.phone_required') || 'Phone number is required');
      return;
    }

    try {
      setSendingCode(true);
      setError(null);
      // If email is provided, use login-time endpoint (no auth required)
      await API.setupSms(phoneNumber, email);
      setCodeSent(true);
    } catch (err: any) {
      console.error('[SMS Setup] Error:', err);
      setError(err?.response?.data?.message || t('sentences:pages.mfa_setup.sms_setup_error') || 'Failed to send verification code');
    } finally {
      setSendingCode(false);
    }
  };

  const handleVerify = async () => {
    // Remove all frontend validation - let backend handle it
    setVerificationAttempted(true);

    try {
      setVerifying(true);
      setError(null);
      
      // verifySms already handles switching (sets preferredMethod to SMS)
      // If email is provided, use login-time endpoint (no auth required)
      const response = await API.verifySms(verificationCode, email);
      
      // If this is login-time setup, the response includes accessToken and user
      // We should handle that in the parent component
      if (email && response.accessToken) {
        // Login-time setup completed - parent should handle the token
      }
      
      // If this is admin/owner setting up their method to enable company MFA, enable it now
      if (enableCompanyMfaAfterSetup) {
        try {
          await API.put(`companies/updateMfa`, { mfa: true });
        } catch (err: any) {
          console.error('[SMS Setup] Error enabling company MFA:', err);
          setError(err?.response?.data?.message || 'Failed to enable company MFA');
          setVerifying(false);
          return;
        }
      }
      
      // Pass response to onSuccess callback (for login-time setup)
      onSuccess(response);
      onClose();
    } catch (err: any) {
      console.error('[SMS Setup] Verification error:', err);
      setError(err?.response?.data?.message || t('sentences:pages.mfa_setup.sms_verify_error') || 'Invalid verification code');
      setVerificationCode('');
    } finally {
      setVerifying(false);
    }
  };

  const handleClose = () => {
    if (!isSwitching || !isAlreadySetUp) {
      setPhoneNumber('');
    }
    setCodeSent(false);
    setVerificationCode('');
    setError(null);
    setVerificationAttempted(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isSwitching 
          ? (t('sentences:pages.mfa_setup.sms_switch_title') || 'Switch to SMS')
          : (t('sentences:pages.mfa_setup.sms_title') || 'Set Up SMS Authentication')}
      </DialogTitle>
      <DialogContent>
        {error && verificationAttempted && (
          <Alert 
            severity="error" 
            sx={{ mb: 2 }} 
            onClose={() => {
              setError(null);
              setVerificationAttempted(false);
            }}
          >
            {error}
          </Alert>
        )}

        {!isSwitching || !isAlreadySetUp ? (
          <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
            {t('sentences:pages.mfa_setup.sms_description') || 
              'Enter your phone number to receive verification codes via text message'}
          </Typography>
        ) : (
          <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
            {t('sentences:pages.mfa_setup.sms_switch_description') || 
              'A verification code has been sent to your phone. Please verify to switch to SMS authentication.'}
          </Typography>
        )}

        {!codeSent ? (
          <Box>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              {t('sentences:pages.mfa_setup.sms_enter_phone') || 'Enter your phone number'}
            </Typography>
            <MuiPhoneNumber
              defaultCountry={'ca'}
              value={phoneNumber}
              onChange={(value) => {
                setPhoneNumber(value as string);
                setError(null);
              }}
              variant="outlined"
              fullWidth
              disableAreaCodes
              disabled={sendingCode}
            />
          </Box>
        ) : (
          <Box>
            <Typography variant="body2" sx={{ mb: 2, fontWeight: 500 }}>
              {t('sentences:pages.mfa_setup.sms_enter_code') || 'Enter the 6-digit code sent to your phone'}
            </Typography>
            <OtpInput
              length={6}
              value={verificationCode}
              onChange={(value) => {
                setVerificationCode(value);
                // Clear error when user types
                if (error) {
                  setError(null);
                }
                setVerificationAttempted(false);
              }}
              disabled={verifying}
              error={false}
              onComplete={() => {
                // Don't auto-submit - user must click VERIFY button
                // Just clear any previous errors
                if (error) {
                  setError(null);
                }
                setVerificationAttempted(false);
              }}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={sendingCode || verifying}>
          {t('verbs.cancel')}
        </Button>
        {!codeSent ? (
          <Button
            onClick={handleSendCode}
            variant="contained"
            disabled={!phoneNumber || sendingCode}
          >
            {sendingCode ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                {t('verbs.sending') || 'Sending...'}
              </>
            ) : (
              t('verbs.send_code') || 'Send Code'
            )}
          </Button>
        ) : (
          <Button
            onClick={handleVerify}
            variant="contained"
            disabled={verifying}
          >
            {verifying ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                {t('verbs.verifying') || 'Verifying...'}
              </>
            ) : (
              t('verbs.verify')
            )}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SmsSetupDialog;

